import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Clock, ArrowRight, Bell } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Kicker, WfButton, Tag } from "@/components/site/primitives";
import { EVENTS_HERO, EVENTS } from "@/content/getInvolved";
import { cn } from "@/lib/utils";

// Events dated today or later are "upcoming"; earlier ones are "past".
const splitEvents = (events, now = new Date()) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const upcoming = [];
  const past = [];
  events.forEach((e) => (new Date(`${e.date}T23:59:59`) >= today ? upcoming : past).push(e));
  upcoming.sort((a, b) => a.date.localeCompare(b.date));
  past.sort((a, b) => b.date.localeCompare(a.date));
  return { upcoming, past };
};

const EventCard = ({ ev, featured = false, past = false }) => (
  <Link
    to={`/events/${ev.slug}`}
    data-testid={`event-card-${ev.slug}`}
    className={cn(
      "group flex h-full flex-col overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
      featured && "lg:grid lg:grid-cols-2"
    )}
  >
    <div className={cn("relative overflow-hidden bg-[var(--wf-cream)]", featured ? "aspect-[16/10] lg:aspect-auto lg:min-h-[24rem]" : "aspect-[16/10]")}>
      <img src={ev.image} alt={ev.imageAlt} loading="lazy" className={cn("h-full w-full object-cover transition-transform duration-[700ms] ease-out motion-safe:group-hover:scale-105", featured && "lg:absolute lg:inset-0", past && "saturate-[0.6]")} />
      <div className="absolute left-5 top-5 rounded-sm bg-[rgba(255,253,248,0.94)] px-4 py-2.5 text-center text-[var(--wf-maroon)] shadow-[var(--wf-shadow-sm)] backdrop-blur-sm">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.14em]">{ev.dateParts.month}</span>
        <span className="block font-serif text-2xl font-semibold leading-none">{ev.dateParts.day}</span>
        <span className="block text-[10px] font-medium text-muted-foreground">{ev.dateParts.year}</span>
      </div>
      <span className="absolute right-5 top-5">{past ? <Tag>Past event</Tag> : <Tag tone="green">{ev.category}</Tag>}</span>
    </div>
    <div className={cn("flex flex-1 flex-col p-6", featured && "lg:p-10")}>
      {ev.subtitle && <span className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--wf-green-text)]">{ev.subtitle}</span>}
      <h3 className={cn("mt-1 font-serif font-semibold text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]", featured ? "text-3xl sm:text-4xl" : "text-2xl")}>{ev.title}</h3>
      <p className="mt-3 flex-1 text-sm leading-6 text-[var(--wf-soil)] sm:text-base">{ev.summary}</p>
      <ul className="mt-5 space-y-1.5 text-sm text-[var(--wf-ink)]">
        <li className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[var(--wf-maroon)]" aria-hidden="true" />{ev.dateLabel}</li>
        <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-[var(--wf-maroon)]" aria-hidden="true" />{ev.time}</li>
        <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[var(--wf-maroon)]" aria-hidden="true" />{ev.location} · Lancaster, MA</li>
      </ul>
      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--wf-maroon)]">{past ? "See recap & details" : "Event details & registration"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></span>
    </div>
  </Link>
);

export default function EventsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { upcoming, past } = useMemo(() => splitEvents(EVENTS), []);
  const [featured, ...rest] = upcoming;

  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...EVENTS_HERO} testid="events-hero" />

        {/* Upcoming */}
        <section id="upcoming" aria-labelledby="events-upcoming-heading" className="wf-section scroll-mt-20 bg-[var(--wf-cream)]">
          <div className="wf-container">
            <Reveal className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <Kicker>Upcoming</Kicker>
                <h2 id="events-upcoming-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Mark your calendar</h2>
              </div>
              <p className="text-sm text-muted-foreground" data-testid="events-upcoming-count">{upcoming.length} upcoming {upcoming.length === 1 ? "event" : "events"}</p>
            </Reveal>

            {upcoming.length === 0 ? (
              <div data-testid="events-empty-state" className="mx-auto mt-10 max-w-lg rounded-sm border border-dashed border-[var(--wf-border)] bg-[var(--wf-paper)] p-10 text-center">
                <Bell className="mx-auto h-8 w-8 text-[var(--wf-moss)]" aria-hidden="true" />
                <p className="mt-3 font-serif text-xl font-semibold text-[var(--wf-ink)]">No events scheduled right now</p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">Join our email list and we’ll let you know the moment the next farm tour or harvest celebration is announced.</p>
                <WfButton as={Link} to="/contact#email-list" withArrow className="mt-6 px-7" data-testid="events-empty-subscribe">Join the email list</WfButton>
              </div>
            ) : (
              <div className="mt-10 space-y-6">
                <Reveal><EventCard ev={featured} featured /></Reveal>
                {rest.length > 0 && (
                  <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {rest.map((ev, i) => (<Reveal key={ev.slug} delay={0.05 * i} as="li" className="h-full"><EventCard ev={ev} /></Reveal>))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Past */}
        {past.length > 0 && (
          <section id="past" aria-labelledby="events-past-heading" className="wf-section bg-white">
            <div className="wf-container">
              <Reveal className="max-w-2xl">
                <Kicker>Past events</Kicker>
                <h2 id="events-past-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">Recent gatherings</h2>
              </Reveal>
              <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="events-past-grid">
                {past.map((ev, i) => (<Reveal key={ev.slug} delay={0.05 * i} as="li" className="h-full"><EventCard ev={ev} past /></Reveal>))}
              </ul>
            </div>
          </section>
        )}

        {/* Host / stay informed */}
        <section aria-labelledby="events-cta-heading" className="bg-[var(--wf-cream)] py-16 sm:py-20 lg:py-24">
          <div className="wf-container">
            <div className="relative overflow-hidden rounded-sm bg-[var(--wf-maroon)] px-6 py-14 text-center sm:px-10 sm:py-16">
              <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--wf-clay)]/30 blur-2xl" />
              <div className="relative mx-auto max-w-2xl">
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">Never miss a farm day</span>
                <h2 id="events-cta-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">Be the first to hear about new events</h2>
                <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.85)]">Event invitations go to our email list first. Want to coordinate a group visit? Get in touch.</p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <WfButton as={Link} to="/contact#email-list" variant="light" withArrow className="px-7" data-testid="events-subscribe-cta">Join the email list</WfButton>
                  <WfButton as={Link} to="/contact" className="border border-white/60 bg-white/10 px-7 text-white backdrop-blur-sm hover:bg-white/20" data-testid="events-contact-cta">Plan a group visit</WfButton>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
